import React, { useCallback, useEffect, useState } from "react";
import PaymentsAppLayout from "../../../Components/Molecules/PaymentsAppLayout";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import PrivateComponentLoadingPage from "../../LoadingPage/PrivateComponentLoadingPage";
import { useUserStore } from "../../../Store/User";
import { PAGINATION_KEYS, usePagination } from "../../../Store/Pagination";
import { User } from "../../../Contracts/User";
import Table, { Column, DataSourceItem } from "../../../Components/Atoms/Table";
import Tag from "../../../Components/Atoms/Tag";
import LoadingPage from "../../LoadingPage";
import TablePaginationFooter from "../../../Components/Atoms/Table/TablePaginationFooter";
import { toLocalDateString } from "../../../Utils/DateUtils";
import UserManagementActionMenu from "./UserManagementActionMenu";
import Modal from "../../../Components/Atoms/Modal";
import { validateEmail } from "../../../Contracts/Common";

const UserManagementPage: React.FC = () => {
  const [userInviteFormOpen, setUserInviteFormOpen] = useState<boolean>(false);

  const { limit, page, setLimit, setPage } = usePagination(PAGINATION_KEYS.USER_MANAGEMENT, {
    limit: 25,
    page: 1
  });

  const {
    isLoading,
    users,
    usersCount,
    getUsers,
    inviteNewUser,
    editUser,
    deleteUser,
    resendInvite
  } = useUserStore();
  const [inviteUserForm, setUserInviteForm] = useState<{
    email: string;
    valid: boolean;
  }>({ email: "", valid: false });

  const columns: Column<User>[] = [
    {
      title: "User Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Type",
      dataIndex: "user_type",
      key: "user_type"
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: d => <Tag color={d.status === "Active" ? "lime" : "blue"}>{d.status}</Tag>
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email"
    },
    {
      title: "Date Joined",
      dataIndex: "date_joined",
      key: "date_joined"
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: d => (
        <UserManagementActionMenu
          user={d.data}
          onDelete={user => deleteUser(getAccessTokenSilently, user)}
          onEdit={async (userId, firstName, lastName) => {
            await editUser(getAccessTokenSilently, userId, firstName, lastName);
          }}
          onResendInvite={userId => resendInvite(getAccessTokenSilently, userId)}
        />
      )
    }
  ];

  const { getAccessTokenSilently } = useAuth0();
  const refreshUsers = useCallback(async () => {
    await getUsers(getAccessTokenSilently);
  }, [getAccessTokenSilently, getUsers]);
  useEffect(() => {
    refreshUsers();
  }, [refreshUsers]);

  const dataSource: DataSourceItem<User>[] = users.map(user => ({
    key: user.id.toString(),
    name: user.user_type === "user" ? `${user.first_name} ${user.last_name}` : "--",
    user_type: user.is_owner
      ? user.is_admin
        ? "Owner & Admin"
        : "Owner"
      : user.is_admin
        ? "Admin"
        : "User",
    status: user.user_type === "user" ? "Active" : "Invited",
    email: user.email,
    date_joined: user.user_type === "user" ? toLocalDateString(user.created_at) : "--",
    data: user
  }));

  const validateForm = (): boolean => {
    const isValid = validateEmail(inviteUserForm.email);
    if (!isValid) {
      setUserInviteForm({ ...inviteUserForm, valid: false });
    }
    return isValid;
  };

  return (
    <PaymentsAppLayout>
      <div className="flex flex-col h-[calc(100vh-9rem)]">
        <div className="relative">
          <div className="flex items-center justify-between">
            <div className="font-semibold mx-2 mb-8">Users</div>
            <button
              className="bg-blue-600 hover:bg-blue-400 text-white py-1 px-2 rounded"
              onClick={() => setUserInviteFormOpen(true)}
            >
              + New
            </button>
          </div>
          <Modal
            isOpen={userInviteFormOpen}
            onClose={() => {
              setUserInviteFormOpen(false);
              setUserInviteForm({ email: "", valid: true });
            }}
            title="Invite a new user"
            okText="Invite"
            disableOkButton={!inviteUserForm.valid}
            onOk={async () => {
              if (!validateForm()) return;
              await inviteNewUser(getAccessTokenSilently, inviteUserForm.email);
              setUserInviteForm({ email: "", valid: true });
              setUserInviteFormOpen(false);
            }}
          >
            <p className="mb-2">Invite a user to your account</p>
            <div className="flex space-x-4 items-center my-2">
              <label>Email</label>
              <input
                type="email"
                placeholder="Enter email address"
                className="border border-gray-200 rounded px-2 py-1"
                value={inviteUserForm.email}
                onChange={e => {
                  const email = e.target.value;
                  setUserInviteForm({
                    ...inviteUserForm,
                    email,
                    valid: validateEmail(email)
                  });
                }}
              />
            </div>
            {!inviteUserForm.valid && !!inviteUserForm.email?.length && (
              <div className="text-red-500 text-sm">Please enter a valid email address</div>
            )}
          </Modal>
        </div>
        <div
          className={`flex flex-grow flex-col overflow-y-auto ${
            !isLoading && "border-t border-gray-100"
          } `}
        >
          {isLoading && users.length === 0 ? (
            <LoadingPage />
          ) : (
            <Table dataSource={dataSource} columns={columns} isLoading={isLoading} />
          )}
        </div>
        <div className="py-2 text-gray-700">
          If you want to cancel your account, please send an email to{" "}
          <a
            className="text-blue-600"
            href="mailto:support@zomentum.com?Subject=Cancel my Zomentum Payments account"
          >
            support@zomentum.com
          </a>
        </div>
        <TablePaginationFooter
          limit={limit}
          setLimit={setLimit}
          setPage={setPage}
          totalRecords={usersCount}
          page={page}
          isLoading={isLoading}
        />
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(UserManagementPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
